/* ============================================================
   GLOBAL.JS - Axiom Theme
   Drawer/modal utilities, navigation scroll, share and cart helpers
   ============================================================ */

(function () {
  'use strict';

  function decodeHtmlString(value) {
    if (typeof value !== 'string' || value.indexOf('&') === -1) return value;
    var textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value;
  }

  function decodeConfigValue(value) {
    if (typeof value === 'string') return decodeHtmlString(value);
    if (Array.isArray(value)) return value.map(decodeConfigValue);
    if (value && typeof value === 'object') {
      Object.keys(value).forEach(function (key) {
        value[key] = decodeConfigValue(value[key]);
      });
    }
    return value;
  }

  var configEl = document.getElementById('AxiomThemeConfig');
  if (configEl) {
    try {
      window.Axiom = Object.assign(
        window.Axiom || {},
        decodeConfigValue(JSON.parse(configEl.textContent || '{}'))
      );
    } catch (error) {
      window.Axiom = window.Axiom || {};
    }
  }

  // One live MediaQueryList: .matches stays current, and it is read for every heading and reveal item.
  var reducedMotionQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  function prefersReducedMotion() {
    return Boolean(reducedMotionQuery && reducedMotionQuery.matches);
  }

  function getMotionValue(name, fallback) {
    var value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return value || fallback;
  }

  function getMotionDuration(name, fallback) {
    var value = getMotionValue(name, fallback || '420ms');
    if (value.endsWith('ms')) return parseFloat(value);
    if (value.endsWith('s')) return parseFloat(value) * 1000;
    return parseFloat(value) || parseFloat(fallback) || 420;
  }

  function parseTimeToMs(value) {
    value = String(value || '').trim();
    if (!value) return 0;
    if (value.endsWith('ms')) return parseFloat(value) || 0;
    if (value.endsWith('s')) return (parseFloat(value) || 0) * 1000;
    return parseFloat(value) || 0;
  }

  function getMotionEase(name, fallback) {
    return getMotionValue(name, fallback || 'cubic-bezier(0.22, 1, 0.36, 1)');
  }

  var assetLoadPromises = {};

  function loadStylesheetOnce(url) {
    if (!url) return Promise.resolve();
    var key = 'style:' + url;
    if (assetLoadPromises[key]) return assetLoadPromises[key];

    var existing = document.querySelector('link[href="' + url + '"]');
    if (existing && (existing.dataset.loaded === 'true' || existing.sheet)) {
      assetLoadPromises[key] = Promise.resolve(existing);
      return assetLoadPromises[key];
    }

    assetLoadPromises[key] = new Promise(function (resolve, reject) {
      var link = existing || document.createElement('link');
      var settled = false;

      function cleanup() {
        link.removeEventListener('load', handleLoad);
        link.removeEventListener('error', handleError);
      }

      function handleLoad() {
        if (settled) return;
        settled = true;
        link.dataset.loaded = 'true';
        cleanup();
        resolve(link);
      }

      function handleError() {
        if (settled) return;
        settled = true;
        cleanup();
        reject(new Error('Failed to load stylesheet: ' + url));
      }

      if (!existing) {
        link.rel = 'stylesheet';
        link.href = url;
      }

      link.addEventListener('load', handleLoad, { once: true });
      link.addEventListener('error', handleError, { once: true });

      if (!existing) {
        document.head.appendChild(link);
        return;
      }

      if (link.sheet) {
        handleLoad();
      }
    });

    return assetLoadPromises[key];
  }

  function loadScriptOnce(url) {
    if (!url) return Promise.resolve();
    var key = 'script:' + url;
    if (assetLoadPromises[key]) return assetLoadPromises[key];

    var existing = document.querySelector('script[src="' + url + '"]');
    if (existing && existing.dataset.loaded === 'true') {
      assetLoadPromises[key] = Promise.resolve(existing);
      return assetLoadPromises[key];
    }

    assetLoadPromises[key] = new Promise(function (resolve, reject) {
      var script = existing || document.createElement('script');
      script.src = url;
      script.defer = true;
      script.onload = function () {
        script.dataset.loaded = 'true';
        resolve(script);
      };
      script.onerror = function () { reject(new Error('Failed to load script: ' + url)); };
      if (!existing) document.body.appendChild(script);
    });

    return assetLoadPromises[key];
  }

  var quickViewAssetsPromise = null;
  var cartControllerPromise = null;
  var countdownTimerPromise = null;
  var revealObserver = null;

  function loadQuickViewAssets() {
    if (window.Axiom && window.Axiom.quickViewAssetsReady) return Promise.resolve();
    if (quickViewAssetsPromise) return quickViewAssetsPromise;

    var modal = document.querySelector('[data-quick-view-modal]');
    if (!modal) return Promise.resolve();

    quickViewAssetsPromise = Promise.all([
      loadStylesheetOnce(modal.dataset.quickViewGalleryCss),
      loadStylesheetOnce(modal.dataset.quickViewCss),
      loadScriptOnce(modal.dataset.quickViewScript)
    ]).then(function () {
      window.Axiom.quickViewAssetsReady = true;
    });

    return quickViewAssetsPromise;
  }

  function initDeferredQuickView() {
    if (!document.querySelector('[data-quick-view-modal]')) return;

    // Theme editor: load the window now, so selecting Quick add can open it (theme-editor.js).
    if (window.Shopify && window.Shopify.designMode) loadQuickViewAssets().catch(function () {});

    function quickViewTriggerFromEvent(event) {
      var target = event.target;
      if (!target || !target.closest) return null;
      var trigger = target.closest('[data-quick-view]');
      if (trigger) return trigger;
      if (!window.matchMedia('(max-width: 900px)').matches) return null;
      var cardLink = target.closest('[data-product-card-link]');
      var card = cardLink && cardLink.closest('[data-product-card]');
      var mode = card && card.dataset.productCardMobileQuickViewMode;
      return mode === 'direct' || mode === 'reveal' ? cardLink : null;
    }

    document.addEventListener('pointerenter', function (event) {
      var trigger = quickViewTriggerFromEvent(event);
      if (!trigger || (window.Axiom && window.Axiom.quickViewAssetsReady)) return;
      loadQuickViewAssets().catch(function () {});
    }, true);

    document.addEventListener('focusin', function (event) {
      var trigger = quickViewTriggerFromEvent(event);
      if (!trigger || (window.Axiom && window.Axiom.quickViewAssetsReady)) return;
      loadQuickViewAssets().catch(function () {});
    });

    document.addEventListener('click', function (event) {
      var trigger = quickViewTriggerFromEvent(event);
      if (!trigger || (window.Axiom && window.Axiom.quickViewAssetsReady)) return;

      event.preventDefault();
      // A card tap that opens quick view shows its pending state while the assets load
      // (component-product-card.css); component-quick-view.js keeps it until the window opens.
      var pendingCard = trigger.matches('[data-product-card-link]') ? trigger.closest('[data-product-card]') : null;
      if (pendingCard && pendingCard.dataset.productCardMobileQuickViewMode !== 'direct') pendingCard = null;
      if (pendingCard) pendingCard.dataset.quickViewLoading = 'true';
      loadQuickViewAssets()
        .then(function () {
          requestAnimationFrame(function () {
            trigger.click();
          });
        })
        .catch(function (error) {
          if (pendingCard) delete pendingCard.dataset.quickViewLoading;
          console.error(error);
        });
    }, true);
  }

  function loadCartController() {
    if (window.Axiom && window.Axiom.cart && typeof window.Axiom.cart.open === 'function') {
      return Promise.resolve(window.Axiom.cart);
    }
    if (cartControllerPromise) return cartControllerPromise;

    var scriptUrl = document.body && document.body.dataset ? document.body.dataset.cartControllerScript : '';
    if (!scriptUrl) return Promise.resolve(null);

    cartControllerPromise = loadScriptOnce(scriptUrl).then(function () {
      return window.Axiom && window.Axiom.cart ? window.Axiom.cart : null;
    });

    return cartControllerPromise;
  }

  function loadSwiperAssets(cssUrl, scriptUrl) {
    if (typeof window.Swiper === 'function') return Promise.resolve(window.Swiper);

    return Promise.all([
      loadStylesheetOnce(cssUrl),
      loadScriptOnce(scriptUrl)
    ]).then(function () {
      return window.Swiper;
    });
  }

  function hasCountdown(root) {
    var scope = root && root.nodeType === 1 ? root : document;
    if (scope.matches && scope.matches('[data-countdown]')) return true;
    return Boolean(scope.querySelector && scope.querySelector('[data-countdown]'));
  }

  function initDeferredCountdownTimer(root) {
    if (!hasCountdown(root)) return Promise.resolve();

    if (window.AxiomCountdownTimer && typeof window.AxiomCountdownTimer.initAll === 'function') {
      window.AxiomCountdownTimer.initAll(root || document);
      return Promise.resolve(window.AxiomCountdownTimer);
    }

    if (!countdownTimerPromise) {
      var scriptUrl = document.body && document.body.dataset ? document.body.dataset.countdownTimerScript : '';
      countdownTimerPromise = loadScriptOnce(scriptUrl).then(function () {
        return window.AxiomCountdownTimer || null;
      });
    }

    return countdownTimerPromise.then(function (controller) {
      if (controller && typeof controller.initAll === 'function') {
        controller.initAll(root || document);
      }
      return controller;
    });
  }

  function resolveScopedRoot(scope) {
    var root = scope;
    if (scope && scope.detail && scope.detail.container) {
      root = scope.detail.container;
    } else if (scope && scope.target && scope.target.nodeType === 1) {
      root = scope.target;
    }

    if (!root || root === document || root === document.documentElement || root === document.body) {
      return document;
    }

    return root.nodeType === 1 ? root : document;
  }

  function isMobileViewport() {
    return Boolean(window.matchMedia && window.matchMedia('(max-width: 767px)').matches);
  }

  function focusWithoutScroll(target) {
    if (!target || typeof target.focus !== 'function') return;

    try {
      target.focus({ preventScroll: true });
    } catch (error) {
      target.focus();
    }
  }

  function openCartAfterBootstrap(trigger) {
    loadCartController()
      .then(function (cartController) {
        if (!cartController || !window.Axiom || window.Axiom.cartType !== 'drawer') return;
        if (typeof cartController.open === 'function') {
          cartController.open(trigger);
        }
      })
      .catch(function (error) {
        console.error(error);
      });
  }

  function initDeferredCartController() {
    document.addEventListener('click', function (event) {
      var trigger = event.target.closest && event.target.closest('[data-cart-drawer-open], [data-open-cart]');
      if (!trigger) return;

      event.preventDefault();
      if (window.Shopify && window.Shopify.actions && typeof window.Shopify.actions.openCart === 'function') {
        window.Shopify.actions.openCart();
        return;
      }
      openCartAfterBootstrap(trigger);
    }, true);
  }

  var revealObservedTargets = new Set();
  var REVEAL_ITEM_SELECTOR = '[data-reveal-item]';
  var REVEAL_GROUP_SELECTOR = '[data-reveal-group]';

  function getRevealRoot(scope) {
    return resolveScopedRoot(scope);
  }

  function normalizeRevealSelector(selector) {
    return selector ? String(selector) : REVEAL_ITEM_SELECTOR;
  }

  function collectMatches(root, selector) {
    if (!root) return [];
    var queryRoot = root === document ? document.documentElement : root;
    var matches = [];

    if (queryRoot.matches && queryRoot.matches(selector)) matches.push(queryRoot);
    queryRoot.querySelectorAll(selector).forEach(function (element) {
      matches.push(element);
    });

    return matches;
  }

  function getRevealElements(container, selector) {
    return collectMatches(container, normalizeRevealSelector(selector));
  }

  function getRevealGroupItems(group, selector) {
    return getRevealElements(group, selector).filter(function (item) {
      var owner = item.closest(REVEAL_GROUP_SELECTOR);
      return owner === group || (item === group && !owner);
    });
  }

  function getRevealGroupOptions(group, options) {
    var resolved = Object.assign({}, options || {});
    if (!group || !window.getComputedStyle) return resolved;

    var styles = window.getComputedStyle(group);
    if (!Number.isFinite(resolved.stagger)) {
      resolved.stagger = parseTimeToMs(styles.getPropertyValue('--reveal-step')) || 70;
    }
    if (!Number.isFinite(resolved.startDelay)) {
      resolved.startDelay = parseTimeToMs(styles.getPropertyValue('--reveal-start'));
    }
    return resolved;
  }

  function animationsAreEnabled() {
    return Boolean(
      document.body
      && document.body.dataset.revealAnimation === 'true'
      && !(window.Shopify && window.Shopify.designMode)
      && !prefersReducedMotion()
    );
  }

  function canAnimateReveal(trigger) {
    if (!animationsAreEnabled()) return false;
    return trigger !== 'scroll' || !isMobileViewport();
  }

  function hasPassedRevealStart(element, startRatio) {
    if (!element || !element.getBoundingClientRect) return false;
    var ratio = Number.isFinite(startRatio) ? startRatio : 0.88;
    return element.getBoundingClientRect().top <= window.innerHeight * ratio;
  }

  function setRevealItemsImmediate(items) {
    items.forEach(function (item) {
      item.removeAttribute('data-reveal-ready');
      item.setAttribute('data-reveal-state', 'active');
      item.style.removeProperty('--reveal-calculated-delay');
    });
  }

  function prepareRevealItems(items, options) {
    options = options || {};
    var step = Number.isFinite(options.stagger) ? options.stagger : 70;
    var startDelay = Number.isFinite(options.startDelay) ? options.startDelay : 0;

    items.forEach(function (item, index) {
      var explicitIndex = parseInt(item.dataset.revealIndex, 10);
      var revealIndex = Number.isFinite(explicitIndex) ? explicitIndex : index;
      var authoredDelay = item.style.getPropertyValue('--reveal-delay');
      var calculatedDelay = authoredDelay
        ? parseTimeToMs(authoredDelay)
        : startDelay + revealIndex * step;
      var delay = Math.max(0, calculatedDelay);

      item.dataset.revealReady = 'true';
      item.style.setProperty('--reveal-calculated-delay', delay + 'ms');
      if (options.reset || item.dataset.revealState !== 'active') {
        item.dataset.revealState = 'inactive';
      }
    });
  }

  function activateRevealItems(items) {
    items.forEach(function (item) {
      item.dataset.revealState = 'active';
    });
  }

  function applyRevealElements(elements, options) {
    options = options || {};
    var trigger = options.trigger || 'manual';
    var pending = elements.filter(Boolean);

    if (!pending.length) return pending;
    if (options.immediate || !canAnimateReveal(trigger)) {
      setRevealItemsImmediate(pending);
      return pending;
    }

    prepareRevealItems(pending, options);
    window.requestAnimationFrame(function () {
      activateRevealItems(pending);
    });

    return pending;
  }

  function revealElements(container, options) {
    options = options || {};
    var root = container || document;
    var isGroup = Boolean(root.matches && root.matches(REVEAL_GROUP_SELECTOR));
    var trigger = options.trigger || (
      isGroup
        ? root.dataset.revealTrigger || 'manual'
        : 'manual'
    );
    var resolvedOptions = isGroup ? getRevealGroupOptions(root, options) : Object.assign({}, options);
    var elements = isGroup
      ? getRevealGroupItems(root, options.selector)
      : getRevealElements(root, options.selector);

    if (isGroup) {
      if (resolvedOptions.immediate || !canAnimateReveal(trigger)) {
        root.dataset.revealState = 'active';
      } else {
        root.dataset.revealState = 'inactive';
        window.requestAnimationFrame(function () {
          root.dataset.revealState = 'active';
        });
      }
    }

    return applyRevealElements(elements, Object.assign(resolvedOptions, { trigger: trigger }));
  }

  function revealScrollTarget(target, immediate) {
    if (!target) return;

    if (target.matches && target.matches(REVEAL_GROUP_SELECTOR)) {
      applyRevealElements(getRevealGroupItems(target), getRevealGroupOptions(target, {
        trigger: 'scroll',
        immediate: immediate
      }));
      target.dataset.revealState = 'active';
      return;
    }

    if (target.matches && target.matches(REVEAL_ITEM_SELECTOR)) {
      applyRevealElements([target], { trigger: 'scroll', stagger: 0, immediate: immediate });
    }
  }

  var smoothFocusRailSelector = [
    '[data-featured-collection-viewport]',
    '[data-shop-by-category-rail]',
    '[data-shop-by-category-vertical-rail]',
    '[data-image-gallery-rail]',
    '[data-collection-list-horizontal-rail]',
    '[data-stories-rail]',
    '[data-testimonials-rail]',
    '[data-upsell-track]',
    '.featured-collection-media--mobile-swipe .featured-collection-media__row',
    '.featured-collection-split--mobile-swipe .featured-collection-split__grid',
    '.recently-scroll'
  ].join(',');

  function smoothFocusRailFocusin(event) {
    var rail = event.currentTarget;
    var target = event.target && event.target.closest ? event.target.closest('a[href], button, input, select, textarea, [tabindex]') : null;
    if (!target || !rail.contains(target)) return;

    var scrollTarget = target;
    if (rail.matches('[data-shop-by-category-rail]')) {
      scrollTarget = target.closest('[data-shop-by-category-item]');
      if (!scrollTarget || !rail.contains(scrollTarget)) return;

      var railRect = rail.getBoundingClientRect();
      var itemRect = scrollTarget.getBoundingClientRect();
      var edgeTolerance = 1;
      var itemIsFullyVisible = itemRect.left >= railRect.left - edgeTolerance
        && itemRect.right <= railRect.right + edgeTolerance;

      if (itemIsFullyVisible) return;
    }

    scrollTarget.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
      behavior: prefersReducedMotion() ? 'auto' : 'smooth'
    });
  }

  function initSmoothFocusRails(scope) {
    var root = resolveScopedRoot(scope);
    var rails = [];
    if (root.matches && root.matches(smoothFocusRailSelector)) rails.push(root);
    if (root.querySelectorAll) rails = rails.concat(Array.prototype.slice.call(root.querySelectorAll(smoothFocusRailSelector)));

    rails.forEach(function (rail) {
      if (rail._axiomSmoothFocusRail) return;
      var onFocusin = smoothFocusRailFocusin.bind(rail);
      rail.addEventListener('focusin', onFocusin);
      rail._axiomSmoothFocusRail = {
        onFocusin: onFocusin
      };
    });
  }

  function cleanupSmoothFocusRails(scope) {
    var root = resolveScopedRoot(scope);
    var rails = [];
    if (root.matches && root.matches(smoothFocusRailSelector)) rails.push(root);
    if (root.querySelectorAll) rails = rails.concat(Array.prototype.slice.call(root.querySelectorAll(smoothFocusRailSelector)));

    rails.forEach(function (rail) {
      var handlers = rail._axiomSmoothFocusRail;
      if (!handlers) return;
      rail.removeEventListener('focusin', handlers.onFocusin);
      delete rail._axiomSmoothFocusRail;
    });
  }

  function ensureRevealObserver() {
    if (revealObserver || !('IntersectionObserver' in window)) return revealObserver;

    revealObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        revealObserver.unobserve(entry.target);
        revealObservedTargets.delete(entry.target);
        delete entry.target.dataset.revealObserved;
        revealScrollTarget(entry.target, false);
      });
    }, { rootMargin: '0px 0px -12% 0px' });

    return revealObserver;
  }

  function observeRevealTarget(target, startRatio) {
    if (!target || target.dataset.revealObserved === 'true' || target.dataset.revealState === 'active') return;

    var targetItems = target.matches(REVEAL_GROUP_SELECTOR) ? getRevealGroupItems(target) : [target];
    if (targetItems.length && targetItems.every(function (item) {
      return item.dataset.revealState === 'active';
    })) {
      target.dataset.revealState = 'active';
      return;
    }

    var isImmediate = !canAnimateReveal('scroll')
      || !('IntersectionObserver' in window)
      || hasPassedRevealStart(target, startRatio);

    if (isImmediate) {
      revealScrollTarget(target, true);
      return;
    }

    prepareRevealItems(targetItems, target.matches(REVEAL_GROUP_SELECTOR)
      ? getRevealGroupOptions(target)
      : { stagger: 70 });
    target.dataset.revealObserved = 'true';
    revealObservedTargets.add(target);
    ensureRevealObserver().observe(target);
  }

  function initNativeReveal(scope) {
    var root = getRevealRoot(scope);
    var queryRoot = root === document ? document : root;
    var scrollGroupSelector = REVEAL_GROUP_SELECTOR + '[data-reveal-trigger="scroll"]';

    collectMatches(queryRoot, scrollGroupSelector).forEach(function (group) {
      observeRevealTarget(group, 0.86);
    });

    collectMatches(queryRoot, REVEAL_ITEM_SELECTOR).forEach(function (item) {
      var owner = item.closest(REVEAL_GROUP_SELECTOR);
      if (owner) return;
      observeRevealTarget(item, 0.88);
    });
  }

  function cleanupRevealScope(scope) {
    var root = getRevealRoot(scope);
    revealObservedTargets.forEach(function (target) {
      if (root !== document && target !== root && !root.contains(target)) return;
      if (revealObserver) revealObserver.unobserve(target);
      revealObservedTargets.delete(target);
      delete target.dataset.revealObserved;
    });
  }

  function initAnimations(scope) {
    initNativeReveal(scope);
  }

  function initInitialAnimations() {
    initNativeReveal(document);
  }

  function initNavScroll() {
    var nav = document.getElementById('mainNav') || document.querySelector('.nav');
    if (!nav) return;
    if (nav.hasAttribute('data-header')) return;

    var ticking = false;
    function update() {
      if (window.scrollY > 60) {
        nav.classList.add('scrolled');
      } else {
        nav.classList.remove('scrolled');
      }
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });

    update();
  }

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  var lastDrawerInputWasKeyboard = false;

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Tab') return;

    lastDrawerInputWasKeyboard = true;
    document.documentElement.classList.add('is-keyboard-focus-navigation');
  });

  document.addEventListener('pointerdown', function () {
    lastDrawerInputWasKeyboard = false;
    document.documentElement.classList.remove('is-keyboard-focus-navigation');
  }, { passive: true });

  document.addEventListener('touchstart', function () {
    lastDrawerInputWasKeyboard = false;
    document.documentElement.classList.remove('is-keyboard-focus-navigation');
  }, { passive: true });

  function getFocusableElements(container) {
    if (!container || !container.querySelectorAll) return [];
    return Array.prototype.slice.call(container.querySelectorAll(FOCUSABLE)).filter(function (element) {
      if (!element || element.disabled || element.getAttribute('aria-hidden') === 'true') return false;
      if (element.closest && element.closest('[inert]')) return false;
      if (typeof window.getComputedStyle === 'function') {
        var style = window.getComputedStyle(element);
        if (style.display === 'none' || style.visibility === 'hidden') return false;
      }
      return element.getClientRects ? element.getClientRects().length > 0 : true;
    });
  }

  function trapFocus(container, options) {
    options = options || {};
    if (!container) return;
    releaseFocus(container);

    container._focusTrapHandler = function (event) {
      if (event.key !== 'Tab') return;
      var focusable = getFocusableElements(container);
      if (!focusable.length) {
        event.preventDefault();
        focusWithoutScroll(container);
        return;
      }

      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      var activeIndex = focusable.indexOf(document.activeElement);

      if (activeIndex === -1) {
        event.preventDefault();
        focusWithoutScroll(event.shiftKey ? last : first);
        return;
      }

      if (event.shiftKey) {
        if (document.activeElement === first) {
          event.preventDefault();
          focusWithoutScroll(last);
        }
      } else if (document.activeElement === last) {
        event.preventDefault();
        focusWithoutScroll(first);
      }
    };

    container.addEventListener('keydown', container._focusTrapHandler);
    if (options.autofocus === false) return;

    var focusable = getFocusableElements(container);
    var focusTarget = options.focusTarget || focusable[0] || container;
    focusWithoutScroll(focusTarget);
  }

  function releaseFocus(container) {
    if (container._focusTrapHandler) {
      container.removeEventListener('keydown', container._focusTrapHandler);
      delete container._focusTrapHandler;
    }
  }

  function themeString(group, key) {
    return ((window.Axiom && window.Axiom[group] && window.Axiom[group][key]) || '').toString();
  }

  function shouldHideDrawerWhenClosed(drawer) {
    return Boolean(drawer && drawer.hasAttribute && drawer.hasAttribute('data-drawer-hide-when-closed'));
  }

  function getDrawerTransitionTarget(drawer) {
    if (!drawer || !drawer.querySelector) return drawer;
    if (drawer.hasAttribute('data-drawer-transition-root')) return drawer;
    return drawer.querySelector('.mobile-drawer__inner, .desktop-sidebar__inner') || drawer.firstElementChild || drawer;
  }

  function clearDrawerLifecycle(drawer) {
    if (!drawer) return;

    if (drawer._drawerOpenFrame) {
      cancelAnimationFrame(drawer._drawerOpenFrame);
      drawer._drawerOpenFrame = null;
    }

    if (drawer._drawerHideTimer) {
      clearTimeout(drawer._drawerHideTimer);
      drawer._drawerHideTimer = null;
    }

    if (drawer._drawerTransitionTarget && drawer._drawerTransitionEndHandler) {
      drawer._drawerTransitionTarget.removeEventListener('transitionend', drawer._drawerTransitionEndHandler);
      drawer._drawerTransitionTarget = null;
      drawer._drawerTransitionEndHandler = null;
    }
  }

  function getDrawerTransitionTimeout(target) {
    if (!target || !window.getComputedStyle) return 0;

    var styles = window.getComputedStyle(target);
    var durations = String(styles.transitionDuration || '').split(',');
    var delays = String(styles.transitionDelay || '').split(',');

    return durations.reduce(function (max, duration, index) {
      var delay = delays[index] || delays[0] || '0s';
      var durationMs = parseTimeToMs(duration);
      var delayMs = parseTimeToMs(delay);
      return Math.max(max, durationMs + delayMs);
    }, 0);
  }

  var pageScrollLocks = new Set();
  var lockedScrollY = 0;

  function getPageScrollY() {
    return pageScrollLocks.size > 0 ? lockedScrollY : (window.scrollY || window.pageYOffset || 0);
  }

  function freezePageScrollPosition() {
    lockedScrollY = window.scrollY || window.pageYOffset || 0;
    // Desktop browsers preserve the current scroll offset with html overflow locking.
    // Fixing the body there moves the page into a new coordinate space and jumps the view.
    if (window.matchMedia('(min-width: 1024px) and (hover: hover)').matches) return;
    document.body.style.position = 'fixed';
    document.body.style.top = '-' + lockedScrollY + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }

  function unfreezePageScrollPosition() {
    if (document.body.style.position !== 'fixed') return;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo(0, lockedScrollY);
  }

  function lockPageScroll(owner) {
    var alreadyLocked = pageScrollLocks.size > 0;
    pageScrollLocks.add(owner || document.documentElement);
    if (alreadyLocked) return;
    freezePageScrollPosition();
    document.documentElement.classList.add('is-scroll-locked');
    if (window.Axiom && typeof window.Axiom.stopSmoothScroll === 'function') {
      window.Axiom.stopSmoothScroll();
    }
  }

  function unlockPageScroll(owner) {
    pageScrollLocks.delete(owner || document.documentElement);
    if (pageScrollLocks.size > 0) return;
    document.documentElement.classList.remove('is-scroll-locked');
    unfreezePageScrollPosition();
    if (window.Axiom && typeof window.Axiom.startSmoothScroll === 'function') {
      window.Axiom.startSmoothScroll();
    }
  }

  // Open drawers and modals, last opened on top. One Escape closes only the top
  // layer: its handler marks the key event handled (preventDefault), so the
  // handlers of the layers below it skip that same event.
  var openLayers = [];

  function pushLayer(layer) {
    removeLayer(layer);
    openLayers.push(layer);
  }

  function removeLayer(layer) {
    var index = openLayers.indexOf(layer);
    if (index !== -1) openLayers.splice(index, 1);
  }

  function isTopLayerEscape(event, layer) {
    // A layer removed from the page while open (section re-render in the theme
    // editor) never runs its close: drop it so it can't block the layers below.
    openLayers = openLayers.filter(function (item) { return item.isConnected; });
    return event.key === 'Escape' && !event.defaultPrevented && openLayers[openLayers.length - 1] === layer;
  }

  function openDrawer(drawer, overlay, options) {
    options = options || {};

    clearDrawerLifecycle(drawer);

    if (drawer._drawerFocusFrame1) cancelAnimationFrame(drawer._drawerFocusFrame1);
    if (drawer._drawerFocusFrame2) cancelAnimationFrame(drawer._drawerFocusFrame2);
    drawer._drawerFocusFrame1 = null;
    drawer._drawerFocusFrame2 = null;

    if (shouldHideDrawerWhenClosed(drawer)) {
      drawer.hidden = false;
      drawer.classList.remove('is-closing');
      drawer._drawerOpenFrame = requestAnimationFrame(function () {
        drawer.classList.add('is-open');
        drawer._drawerOpenFrame = null;
      });
    } else {
      drawer.classList.add('is-open');
    }

    drawer.setAttribute('aria-hidden', 'false');
    drawer.removeAttribute('inert');
    if (options.returnFocus) drawer._drawerReturnFocus = options.returnFocus;
    if (overlay) {
      if (overlay.hasAttribute('data-drawer-hide-when-closed')) overlay.hidden = false;
      overlay.classList.add('is-open');
    }
    if (options.lockScroll !== false) lockPageScroll(drawer);
    document.dispatchEvent(new CustomEvent('theme:drawer:open', {
      detail: {
        drawer: drawer,
        overlay: overlay
      }
    }));

    var shouldAutofocus = typeof options.autofocus === 'boolean' ? options.autofocus : lastDrawerInputWasKeyboard;
    if (shouldAutofocus) {
      drawer._drawerFocusFrame1 = requestAnimationFrame(function () {
        drawer._drawerFocusFrame2 = requestAnimationFrame(function () {
          trapFocus(drawer, { focusTarget: options.focusTarget });
          drawer._drawerFocusFrame1 = null;
          drawer._drawerFocusFrame2 = null;
        });
      });
    } else if (options.trapFocus === true) {
      drawer._drawerFocusFrame1 = requestAnimationFrame(function () {
        trapFocus(drawer, { autofocus: false });
        drawer._drawerFocusFrame1 = null;
      });
    }

    if (drawer._escHandler) {
      document.removeEventListener('keydown', drawer._escHandler);
    }
    pushLayer(drawer);
    drawer._escHandler = function (event) {
      if (!isTopLayerEscape(event, drawer)) return;
      event.preventDefault();
      if (typeof options.onEscape === 'function') options.onEscape(event);
      else closeDrawer(drawer, overlay);
    };
    document.addEventListener('keydown', drawer._escHandler);
  }

  function closeDrawer(drawer, overlay, options) {
    options = options || {};

    clearDrawerLifecycle(drawer);

    if (drawer._drawerFocusFrame1) cancelAnimationFrame(drawer._drawerFocusFrame1);
    if (drawer._drawerFocusFrame2) cancelAnimationFrame(drawer._drawerFocusFrame2);
    drawer._drawerFocusFrame1 = null;
    drawer._drawerFocusFrame2 = null;

    drawer.classList.remove('is-open');
    if (shouldHideDrawerWhenClosed(drawer) && !drawer.hidden) {
      drawer.classList.add('is-closing');
    }
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('inert', '');
    if (overlay) overlay.classList.remove('is-open');
    if (options.lockScroll !== false) unlockPageScroll(drawer);
    document.dispatchEvent(new CustomEvent('theme:drawer:close', {
      detail: {
        drawer: drawer,
        overlay: overlay
      }
    }));

    releaseFocus(drawer);
    if (drawer._drawerReturnFocus && drawer._drawerReturnFocus.isConnected) {
      focusWithoutScroll(drawer._drawerReturnFocus);
    }
    delete drawer._drawerReturnFocus;

    if (drawer._escHandler) {
      document.removeEventListener('keydown', drawer._escHandler);
      delete drawer._escHandler;
    }
    removeLayer(drawer);

    if (shouldHideDrawerWhenClosed(drawer)) {
      var transitionTarget = getDrawerTransitionTarget(drawer);
      var completeClose = function () {
        if (drawer.getAttribute('aria-hidden') !== 'true' || drawer.classList.contains('is-open')) return;
        clearDrawerLifecycle(drawer);
        drawer.classList.remove('is-closing');
        drawer.hidden = true;
        if (overlay && overlay.hasAttribute('data-drawer-hide-when-closed')) overlay.hidden = true;
      };
      var timeout = getDrawerTransitionTimeout(transitionTarget);

      drawer._drawerTransitionTarget = transitionTarget;
      drawer._drawerTransitionEndHandler = function (event) {
        if (event.target !== transitionTarget || event.propertyName !== 'transform') return;
        completeClose();
      };

      transitionTarget.addEventListener('transitionend', drawer._drawerTransitionEndHandler);
      drawer._drawerHideTimer = setTimeout(completeClose, Math.max(timeout + 80, 120));
    }
  }

  // options.onEscape: what Escape does when this modal is the top layer
  // (default: closeModal), as for openDrawer. options.autofocus false and
  // options.lockScroll false (also as for openDrawer) open it in the theme
  // editor's preview: no focus taken from the editor, no scroll lock.
  function openModal(modal, overlay, options) {
    options = options || {};
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    modal.removeAttribute('inert');
    if (overlay) overlay.classList.add('is-open');
    if (options.lockScroll !== false) lockPageScroll(modal);
    trapFocus(modal, { autofocus: options.autofocus });
    document.dispatchEvent(new CustomEvent('theme:modal:open', {
      detail: {
        modal: modal,
        overlay: overlay
      }
    }));

    if (modal._escHandler) document.removeEventListener('keydown', modal._escHandler);
    pushLayer(modal);
    modal._escHandler = function (event) {
      if (!isTopLayerEscape(event, modal)) return;
      event.preventDefault();
      if (typeof options.onEscape === 'function') options.onEscape(event);
      else closeModal(modal, overlay);
    };
    document.addEventListener('keydown', modal._escHandler);
  }

  function closeModal(modal, overlay) {
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    modal.setAttribute('inert', '');
    if (overlay) overlay.classList.remove('is-open');
    unlockPageScroll(modal);
    releaseFocus(modal);
    document.dispatchEvent(new CustomEvent('theme:modal:close', {
      detail: {
        modal: modal,
        overlay: overlay
      }
    }));

    if (modal._escHandler) {
      document.removeEventListener('keydown', modal._escHandler);
      delete modal._escHandler;
    }
    removeLayer(modal);
  }

  function initShareButtons() {
    document.querySelectorAll('[data-share], [data-share-btn]').forEach(function (button) {
      if (button.dataset.shareBound === 'true') return;
      button.dataset.shareBound = 'true';

      button.addEventListener('click', async function () {
        var url = button.dataset.shareUrl || button.dataset.url || window.location.href;
        var title = button.dataset.shareTitle || button.dataset.title || document.title;

        if (navigator.share) {
          try {
            await navigator.share({ title: title, url: url });
          } catch (error) {}
          return;
        }

        await navigator.clipboard.writeText(url);
        if (button.dataset.sharePreserveContent === 'true') {
          var originalLabel = button.getAttribute('aria-label') || '';
          button.setAttribute('aria-label', button.dataset.shareCopied || 'Link copied');
          setTimeout(function () {
            button.setAttribute('aria-label', originalLabel);
          }, 2000);
          return;
        }

        var originalText = button.textContent;
        button.textContent = button.dataset.shareCopied || 'Link copied';
        setTimeout(function () {
          button.textContent = originalText;
        }, 2000);
      });
    });
  }

  function formatWithDelimiters(cents, precision, thousands, decimal) {
    var amount = Number(cents || 0);
    if (!Number.isFinite(amount)) amount = 0;

    var number = (amount / 100).toFixed(Math.max(0, precision));
    var parts = number.split('.');
    var dollars = parts[0];
    var centsPart = parts[1] ? decimal + parts[1] : '';

    dollars = dollars.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return dollars + centsPart;
  }

  function formatMoneyByPlaceholder(cents, placeholder) {
    switch (placeholder) {
      case 'amount':
        return formatWithDelimiters(cents, 2, ',', '.');
      case 'amount_no_decimals':
        return formatWithDelimiters(cents, 0, ',', '.');
      case 'amount_with_comma_separator':
        return formatWithDelimiters(cents, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return formatWithDelimiters(cents, 0, '.', ',');
      case 'amount_with_space_separator':
        return formatWithDelimiters(cents, 2, ' ', ',');
      case 'amount_no_decimals_with_space_separator':
        return formatWithDelimiters(cents, 0, ' ', ',');
      case 'amount_with_period_and_space_separator':
        return formatWithDelimiters(cents, 2, ' ', '.');
      case 'amount_with_apostrophe_separator':
        return formatWithDelimiters(cents, 2, "'", '.');
      default:
        return formatWithDelimiters(cents, 2, ',', '.');
    }
  }

  function formatMoney(cents) {
    var amount = Number(cents || 0);
    var format = (window.Axiom && window.Axiom.moneyFormat) || '{{amount}}';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var placeholderMatch = format.match(placeholderRegex);
    var placeholder = placeholderMatch ? placeholderMatch[1] : 'amount';

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(amount, format);
    }

    return format.replace(placeholderRegex, formatMoneyByPlaceholder(amount, placeholder));
  }

  function getCartPageUrl() {
    return (window.Axiom && window.Axiom.routes && window.Axiom.routes.cart) || '/cart';
  }

  function isDrawerCartMode() {
    return !window.Axiom || (window.Axiom.cartType || 'drawer') === 'drawer';
  }

  var cartStatePromise = null;

  function fetchCartState(forceRefresh) {
    if (!forceRefresh && cartStatePromise) return cartStatePromise;

    if (!window.AxiomStandardStorefront || typeof window.AxiomStandardStorefront.getCart !== 'function') {
      return Promise.reject(new Error('Shopify.actions.getCart is not available.'));
    }

    cartStatePromise = window.AxiomStandardStorefront.getCart()
      .finally(function () {
        cartStatePromise = null;
      });

    return cartStatePromise;
  }

  function setCartDrawerLoading(isLoading) {
    var drawer = document.getElementById('cart-drawer');
    if (drawer) drawer.classList.toggle('is-loading', Boolean(isLoading));
  }

  function renderCartDrawer(cart) {
    if (window.Axiom && window.Axiom.cart && typeof window.Axiom.cart.render === 'function') {
      window.Axiom.cart.render(cart);
    }
    return cart;
  }

  function openCartDrawer() {
    if (!isDrawerCartMode()) return Promise.resolve(null);

    function open(controller) {
      var cart = controller || (window.Axiom && window.Axiom.cart);
      if (cart && typeof cart.open === 'function') cart.open();
      return cart || null;
    }

    if (window.Axiom && window.Axiom.cart && typeof window.Axiom.cart.open === 'function') {
      return Promise.resolve(open(window.Axiom.cart));
    }

    return loadCartController().then(open);
  }

  function fetchLatestCart() {
    if (window.Axiom && window.Axiom.cart && typeof window.Axiom.cart.fetch === 'function') {
      return window.Axiom.cart.fetch();
    }
    return fetchCartState(true);
  }

  function initCartActions() {
    document.addEventListener('shopify:cart:error', function () {
      document.querySelectorAll('[data-atc-btn].loading').forEach(function (button) {
        button.classList.remove('loading');
      });

      document.querySelectorAll('[data-qv-atc][data-loading="true"]').forEach(function (button) {
        button.dataset.loading = 'false';
        button.disabled = false;
        var nextLabel = button.dataset.activeLabel || button.dataset.addLabel || themeString('productStrings', 'addToCart');
        if (!window.Axiom.setMotionLabel || !window.Axiom.setMotionLabel(button, nextLabel, 'button')) {
          button.textContent = nextLabel;
        }
      });
    });
  }

  function isCartDrawerOpen() {
    var drawer = document.getElementById('cart-drawer');
    return Boolean(drawer && drawer.getAttribute('aria-hidden') === 'false');
  }

  function cartCountValue(element) {
    return element.querySelector('[data-cart-count-value]') || element;
  }

  function playCartCountAnimation(element) {
    if (!element.classList.contains('cart-count--counter') || prefersReducedMotion()) return;
    element.classList.remove('cart-count--animating');
    void element.offsetWidth;
    element.classList.add('cart-count--animating');
    var valueEl = cartCountValue(element);
    function onEnd(event) {
      if (event && event.target !== valueEl && event.target !== element) return;
      element.classList.remove('cart-count--animating');
      element.removeEventListener('animationend', onEnd);
    }
    element.addEventListener('animationend', onEnd);
  }

  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (element) {
      var valueEl = cartCountValue(element);
      var previous = parseInt(valueEl.textContent, 10);
      var next = Number(count);
      if (!Number.isFinite(next)) next = 0;
      valueEl.textContent = next;
      element.style.display = next > 0 ? '' : 'none';
      if (next > 0 && previous !== next) playCartCountAnimation(element);
    });

    document.querySelectorAll('.nav-actions__btn--cart').forEach(function (cartButton) {
      // The button's accessible name comes from its content (label + [data-cart-count-value] above).
      var badgeStyle = cartButton.dataset.cartBadgeStyle || 'dot';

      if (badgeStyle === 'dot' && count > 0) {
        if (!cartButton.querySelector('[data-cart-dot]')) {
          var dot = document.createElement('span');
          dot.className = 'cart-dot';
          dot.setAttribute('data-cart-dot', '');
          cartButton.insertBefore(dot, cartButton.querySelector('[data-cart-count]'));
        }
      } else {
        cartButton.querySelectorAll('[data-cart-dot]').forEach(function (dot) {
          dot.remove();
        });
      }
    });

    document.querySelectorAll('.cart-dot').forEach(function (dot) {
      dot.style.display = count > 0 ? '' : 'none';
    });

    document.querySelectorAll('[data-cart-drawer-count]').forEach(function (element) {
      var next = Number(count);
      if (!Number.isFinite(next)) next = 0;
      element.textContent = ' (' + next + ')';
      element.hidden = next <= 0;
    });
  }

  // Chrome cannot run an animation on the compositor while its element is off-screen, so infinite
  // decorative loops (marquees, hotspot pulses, media rails) are ticked on the main thread and force
  // a full re-layerize of the page on every frame. Sections outside the viewport carry
  // data-offscreen, and each component's CSS pauses its own loop with it.
  var sectionVisibilityObserver = null;

  function observeSectionVisibility(root) {
    if (!('IntersectionObserver' in window)) return;
    if (!sectionVisibilityObserver) {
      sectionVisibilityObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          entry.target.toggleAttribute('data-offscreen', !entry.isIntersecting);
        });
      });
    }

    var scope = root && root.querySelectorAll ? root : document;
    var sections = Array.prototype.slice.call(scope.querySelectorAll('.shopify-section'));
    if (scope.classList && scope.classList.contains('shopify-section')) sections.unshift(scope);
    sections.forEach(function (section) {
      sectionVisibilityObserver.observe(section);
    });
  }

  function unobserveSectionVisibility(event) {
    if (sectionVisibilityObserver && event.target) sectionVisibilityObserver.unobserve(event.target);
  }

  function initScrollToTop() {
    var button = document.querySelector('[data-scroll-to-top]');
    if (!button || button.dataset.initialized === 'true') return;
    button.dataset.initialized = 'true';

    var ticking = false;
    var threshold = parseInt(button.dataset.scrollThreshold, 10);
    if (!Number.isFinite(threshold)) threshold = 420;
    var reduceMotion = prefersReducedMotion();

    function updateVisibility() {
      button.classList.toggle('is-visible', window.scrollY > threshold);
      ticking = false;
    }

    // A 1px marker sits at the threshold; the button shows once the marker has
    // scrolled above the viewport. The observer reports that without reading
    // scrollY on every scroll frame, which forces a layout mid-scroll.
    if ('IntersectionObserver' in window) {
      var marker = document.createElement('div');
      marker.setAttribute('aria-hidden', 'true');
      marker.setAttribute('data-scroll-to-top-marker', '');
      marker.style.cssText = 'position:absolute;top:' + threshold + 'px;left:0;width:1px;height:1px;pointer-events:none;visibility:hidden;';
      document.body.appendChild(marker);
      new IntersectionObserver(function (entries) {
        var entry = entries[entries.length - 1];
        button.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      }).observe(marker);
    } else {
      window.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(updateVisibility);
      }, { passive: true });
      updateVisibility();
    }

    button.addEventListener('click', function () {
      scrollToTarget(0, { immediate: reduceMotion });
    });
  }

  function initSkipLink() {
    var skipLink = document.querySelector('[data-skip-link]');
    if (!skipLink || skipLink.dataset.initialized === 'true') return;
    skipLink.dataset.initialized = 'true';

    skipLink.addEventListener('click', function (event) {
      var targetSelector = skipLink.getAttribute('href');
      var target = targetSelector ? document.querySelector(targetSelector) : null;
      if (!target) return;

      event.preventDefault();

      var hadTabindex = target.hasAttribute('tabindex');
      if (!hadTabindex) target.setAttribute('tabindex', '-1');

      target.focus();

      if (!hadTabindex) {
        target.addEventListener('blur', function () {
          target.removeAttribute('tabindex');
        }, { once: true });
      }
    });
  }

  function scrollToTarget(target, options) {
    var settings = options || {};

    if (window.lenis && typeof window.lenis.scrollTo === 'function' && !settings.native) {
      window.lenis.scrollTo(target, {
        immediate: Boolean(settings.immediate),
        offset: settings.offset || 0
      });
      return;
    }

    if (typeof target === 'string') {
      target = target === 'top' ? 0 : document.querySelector(target);
    }

    if (target && target.nodeType === 1) {
      var rect = target.getBoundingClientRect();
      target = rect.top + (window.pageYOffset || document.documentElement.scrollTop || 0) + (settings.offset || 0);
    }

    window.scrollTo({
      top: typeof target === 'number' ? target : 0,
      behavior: settings.immediate || prefersReducedMotion() ? 'auto' : 'smooth'
    });
  }

  window.Axiom = Object.assign(window.Axiom || {}, {
    openDrawer: openDrawer,
    closeDrawer: closeDrawer,
    openModal: openModal,
    closeModal: closeModal,
    lockPageScroll: lockPageScroll,
    unlockPageScroll: unlockPageScroll,
    getPageScrollY: getPageScrollY,
    trapFocus: trapFocus,
    releaseFocus: releaseFocus,
    prefersReducedMotion: prefersReducedMotion,
    getMotionDuration: getMotionDuration,
    getMotionEase: getMotionEase,
    initAnimations: initAnimations,
    revealElements: revealElements,
    initShareButtons: initShareButtons,
    updateCartCount: updateCartCount,
    formatMoney: formatMoney,
    fetchCartState: fetchCartState,
    openCartDrawer: openCartDrawer,
    renderCartDrawer: renderCartDrawer,
    isCartDrawerOpen: isCartDrawerOpen,
    loadCartController: loadCartController,
    loadStylesheetOnce: loadStylesheetOnce,
    loadScriptOnce: loadScriptOnce,
    loadSwiperAssets: loadSwiperAssets,
    initDeferredCountdownTimer: initDeferredCountdownTimer,
    scrollTo: scrollToTarget
  });

  document.addEventListener('DOMContentLoaded', function () {
    observeSectionVisibility(document);
    initInitialAnimations();
    initSmoothFocusRails(document);
    initNavScroll();
    initShareButtons();
    initCartActions();
    initScrollToTop();
    initSkipLink();
    initDeferredQuickView();
    initDeferredCartController();
    initDeferredCountdownTimer(document).catch(function () {});
  });

  document.addEventListener('shopify:section:load', initAnimations);
  document.addEventListener('shopify:section:load', function (event) {
    observeSectionVisibility(event.target);
  });
  document.addEventListener('shopify:section:unload', unobserveSectionVisibility);
  document.addEventListener('shopify:section:load', initSmoothFocusRails);
  document.addEventListener('axiom:section-rendered', initAnimations);
  document.addEventListener('axiom:section-rendered', initSmoothFocusRails);
  document.addEventListener('shopify:section:unload', cleanupRevealScope);
  document.addEventListener('shopify:section:unload', cleanupSmoothFocusRails);
  document.addEventListener('shopify:section:load', function (event) {
    initDeferredCountdownTimer(event.target || document).catch(function () {});
  });
  document.addEventListener('axiom:section-rendered', function (event) {
    initDeferredCountdownTimer(resolveScopedRoot(event)).catch(function () {});
  });
})();

/* Shopify's <shopify-account> keeps its closed sign-in dialog only hidden: the
   skeleton inside runs endless animations, which makes an idle page render
   every frame. Take the closed dialog out of rendering, and put it back as it
   opens (show / showModal are wrapped so focus still lands inside). */
(function () {
  'use strict';

  if (!window.customElements || !window.MutationObserver) return;

  function quietAccountMenu(host) {
    var root = host.shadowRoot;
    if (!root || host._axiomQuiet) return;
    var dialog = root.querySelector('dialog');
    if (!dialog) {
      // The component renders its dialog after it upgrades.
      var wait = new MutationObserver(function () {
        if (!root.querySelector('dialog')) return;
        wait.disconnect();
        quietAccountMenu(host);
      });
      wait.observe(root, { childList: true, subtree: true });
      return;
    }
    host._axiomQuiet = true;
    var sync = function () {
      dialog.style.display = dialog.open || dialog.classList.contains('closing') ? '' : 'none';
    };
    ['showModal', 'show'].forEach(function (name) {
      var open = dialog[name];
      if (typeof open !== 'function') return;
      dialog[name] = function () {
        dialog.style.display = '';
        return open.apply(this, arguments);
      };
    });
    new MutationObserver(sync).observe(dialog, { attributes: true, attributeFilter: ['open', 'class'] });
    sync();
  }

  function quietAccountMenus() {
    document.querySelectorAll('shopify-account').forEach(quietAccountMenu);
  }

  customElements.whenDefined('shopify-account').then(quietAccountMenus);
  document.addEventListener('shopify:section:load', function () {
    customElements.whenDefined('shopify-account').then(quietAccountMenus);
  });

  /* Scroll rails ([data-scroll-region]): while one actually scrolls (mostly on phones) it takes
     keyboard focus so arrow keys can scroll it, and gets a region label from its section heading.
     When it fits, it leaves the tab order again. */
  function syncScrollRegion(el) {
    var scrolls = el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1;
    if (scrolls) {
      if (!el.hasAttribute('tabindex')) {
        el.setAttribute('tabindex', '0');
        el.setAttribute('data-scroll-region-focusable', '');
      }
      if (!el.hasAttribute('role')) {
        el.setAttribute('role', 'region');
        el.setAttribute('data-scroll-region-role', '');
      }
      if (!el.hasAttribute('aria-label') && !el.hasAttribute('aria-labelledby')) {
        var section = el.closest('.shopify-section') || el.parentElement;
        var heading = section && section.querySelector('h1, h2, h3');
        var label = heading ? heading.textContent.replace(/\s+/g, ' ').trim() : '';
        if (label) {
          el.setAttribute('aria-label', label);
          el.setAttribute('data-scroll-region-label', '');
        }
      }
    } else {
      if (el.hasAttribute('data-scroll-region-focusable')) {
        el.removeAttribute('tabindex');
        el.removeAttribute('data-scroll-region-focusable');
      }
      if (el.hasAttribute('data-scroll-region-role')) {
        el.removeAttribute('role');
        el.removeAttribute('data-scroll-region-role');
      }
      if (el.hasAttribute('data-scroll-region-label')) {
        el.removeAttribute('aria-label');
        el.removeAttribute('data-scroll-region-label');
      }
    }
  }

  var scrollRegionObserver = 'ResizeObserver' in window
    ? new ResizeObserver(function (entries) { entries.forEach(function (entry) { syncScrollRegion(entry.target); }); })
    : null;

  function initScrollRegions(scope) {
    (scope || document).querySelectorAll('[data-scroll-region]').forEach(function (el) {
      if (el._axiomScrollRegion) return;
      el._axiomScrollRegion = true;
      syncScrollRegion(el);
      if (scrollRegionObserver) scrollRegionObserver.observe(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initScrollRegions(); });
  } else {
    initScrollRegions();
  }
  document.addEventListener('shopify:section:load', function (event) { initScrollRegions(event.target); });
})();
